import { useQuery } from '@tanstack/react-query';
import { UserPaymentDetailsQueryKeys } from '../../../queryKeys';
import { UseUserPaymentDetailsMeQueryKeyProps } from './props';
import { AxiosError, AxiosInstance } from 'axios';
import { fetchUserPaymentDetailsMe } from './service';
import {
  GenericError,
  PaginatedResponse,
  UserPaymentDetails,
} from '@starsoft/common/models';
import { useState } from 'react';
import { useAxiosInstance, usePaginatedQuery } from '@starsoft/common/hooks';
import {
  UserPaymentDetailsFetchTypeProps,
  UseUserPaymentDetailsMeProps,
} from '../props';
import { UserPaymentDetailsInputDto } from '@starsoft/common/dto';

/**
 * Custom hook to fetch paginated user payment details for the current user
 *
 * This hook handles fetching payment details with pagination support, filtering by payment type,
 * and managing the query state. It uses React Query for data fetching and caching.
 *
 * @template T - Type extending UserPaymentDetailsFetchTypeProps to specify valid payment types
 * @param {UseUserPaymentDetailsMeProps<T>} options - Optional configuration object
 * @param {boolean} options.enabled - Optional flag to enable/disable the query (defaults to true)
 * @param {T} options.paymentType - Optional payment type to filter results
 *
 * @returns {Object} An object containing:
 *   - data: The paginated payment details response
 *   - pagination callbacks from usePaginatedQuery hook
 *   - isPending: Boolean indicating if fetch is in progress (excluding idle state)
 *   - isFetching: Boolean indicating if a fetch is in progress
 *   - isError: Boolean indicating if an error occurred
 *   - error: Any error that occurred during fetch
 *   - refetch: Function to manually trigger a refetch
 */
export function useUserPaymentDetailsMeDefault<
  T extends UserPaymentDetailsFetchTypeProps,
>(options?: UseUserPaymentDetailsMeProps<T>) {
  const [page, setPage] = useState<number>(1);
  const { enabled = true, paymentType } = options ?? {};
  const axiosInstance: AxiosInstance = useAxiosInstance();

  // Prepare filter object for pagination and type filtering
  const filter: UserPaymentDetailsInputDto = {
    typeId: paymentType,
    page,
    limit: 10,
  };

  const queryKey: UseUserPaymentDetailsMeQueryKeyProps = [
    UserPaymentDetailsQueryKeys.MeDefault,
    filter,
  ];

  const { data, isPending, isError, error, refetch, isFetching, fetchStatus } =
    useQuery<
      PaginatedResponse<UserPaymentDetails>,
      AxiosError<GenericError>,
      PaginatedResponse<UserPaymentDetails>,
      UseUserPaymentDetailsMeQueryKeyProps
    >({
      enabled,
      queryKey,
      queryFn: queryContext =>
        fetchUserPaymentDetailsMe(queryContext, axiosInstance),
    });

  // Setup pagination handling with the usePaginatedQuery hook
  const callbacks = usePaginatedQuery({
    data,
    page,
    setPage,
    queryParams: {
      paymentType,
    },
  });

  return {
    data,
    ...callbacks,
    isPending: isPending && fetchStatus !== 'idle',
    isFetching,
    isError,
    error,
    refetch,
  };
}
